import { _ as __rest, a as __assign } from './tslib.es6-01f7d394.js';
import { R as ResizeObserver } from './ResizeObserver-85c08f26.js';
import { c as classNames } from './index-8a7d9ef9.js';
import { f as focusTrapReact, d as defaultFocusTrapOptions } from './focusTrap-77103721.js';
import * as React from 'react';
import { useEffect } from 'react';
import { useTransition, animated } from 'react-spring';
import { S as StackingContextProvider } from './useOrchard-2c518157.js';

var styles = {"noscroll":"Modal_noscroll_db8f3670","modal":"Modal_modal_db8f3670","background":"Modal_background_db8f3670","content":"Modal_content_db8f3670","pinned":"Modal_pinned_db8f3670","transparent":"Modal_transparent_db8f3670"};

var noScrollClassName = "orc-modal--noscroll";
function measureScrollBarWidth(entries, isOpen) {
    var result;
    // Skip scroll bar width measurement if modal is open
    if (isOpen)
        return;
    // Measure scroll bar width
    for (var _i = 0, entries_1 = entries; _i < entries_1.length; _i++) {
        var entry = entries_1[_i];
        var measuredWidth = self.innerWidth - entry.borderBoxSize[0].inlineSize;
        var cs = window.getComputedStyle(entry.target);
        var paddingRight = parseInt(cs.paddingRight, 10);
        result = measuredWidth;
        if (!isNaN(paddingRight)) {
            result += paddingRight;
        }
    }
    return result;
}
function Modal(_a) {
    var className = _a.className, children = _a.children, isHideBackgroundOverlay = _a.isHideBackgroundOverlay, isPinned = _a.isPinned, onClose = _a.onClose, focusTrapOptions = _a.focusTrapOptions, _b = _a.trapFocus, trapFocus = _b === void 0 ? true : _b, otherProps = __rest(_a, ["className", "children", "isHideBackgroundOverlay", "isPinned", "onClose", "focusTrapOptions", "trapFocus"]);
    var _c = React.useState(0), scrollBarWidth = _c[0], setScrollBarWidth = _c[1];
    var _d = React.useState(false), portalActive = _d[0], setPortalActive = _d[1];
    var child = children ? React.Children.only(children) : null;
    var isOpen = Boolean(child);
    useEffect(function () {
        if (isOpen) {
            setPortalActive(true);
        }
    }, [isOpen]);
    var removeBodyStyle = function () {
        document.body.classList.remove(styles.noscroll);
        document.body.classList.remove(noScrollClassName);
        document.body.style.removeProperty("padding-right");
    };
    var handleRest = React.useCallback(function () {
        !isOpen && removeBodyStyle();
        setPortalActive(isOpen);
    }, [isOpen]);
    var transition = useTransition(children, {
        key: function (item) { return item === null || item === void 0 ? void 0 : item.key; },
        from: {
            opacity: 0,
        },
        enter: {
            opacity: 1,
        },
        leave: {
            opacity: 0,
            position: "absolute",
            left: 0,
            top: 0,
            overflow: "hidden",
        },
        onRest: handleRest,
    });
    React.useEffect(function () {
        var resizeObserver = new ResizeObserver(function (entries) {
            var scrollBarWidthResult = measureScrollBarWidth(entries, isOpen);
            if (scrollBarWidthResult) {
                setScrollBarWidth(scrollBarWidthResult);
            }
        });
        resizeObserver.observe(document.body);
        return function () {
            resizeObserver.unobserve(document.body);
        };
    }, [isOpen]);
    // On unmount
    React.useEffect(function () { return function () { return removeBodyStyle(); }; }, []);
    var handleKeyDown = React.useCallback(function (e) {
        if (onClose && e.key === "Escape") {
            onClose(e);
        }
    }, [onClose]);
    React.useEffect(function () {
        if (!onClose)
            return;
        if (isOpen) {
            window.addEventListener("keydown", handleKeyDown);
        }
        else {
            window.removeEventListener("keydown", handleKeyDown);
        }
        return function () { return window.removeEventListener("keydown", handleKeyDown); };
    }, [handleKeyDown, onClose, isOpen]);
    React.useEffect(function () {
        if (isOpen) {
            document.body.classList.add(styles.noscroll);
            document.body.classList.add(noScrollClassName);
            document.body.style.paddingRight = "".concat(scrollBarWidth, "px");
        }
    }, [isOpen, scrollBarWidth]);
    var handleOutsideClick = function (event) {
        if (event.target === event.currentTarget) {
            onClose(event);
        }
    };
    return (React.createElement(StackingContextProvider, { style: { pointerEvents: isOpen ? "auto" : "none" }, className: classNames(styles.modal, className), portalActive: portalActive }, transition(function (style, item) {
        var _a, _b;
        return item && (React.createElement(animated.div, __assign({ role: "document", className: classNames(styles.content, (_a = {}, _a[styles.transparent] = isHideBackgroundOverlay, _a), (_b = {}, _b[styles.pinned] = isPinned, _b)), style: style, onMouseDown: handleOutsideClick, onTouchStart: handleOutsideClick }, otherProps),
            React.createElement(StackingContextProvider, { noPortal: true },
                React.createElement(focusTrapReact, { focusTrapOptions: __assign(__assign({}, defaultFocusTrapOptions), focusTrapOptions), active: trapFocus },
                    React.createElement("div", null, item)))));
    })));
}

export { Modal as M };
